import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

interface PaymentDetails {
  orderId?: string;
  packageName?: string;
  amount?: number;
  paymentMethod?: string;
  timestamp?: string;
  paymentUrl?: string;
  phrase?: string;
  reference?: string;
  entity?: string;
}

const ThankYouPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isLoading } = useAuth();

  const paymentDetails: PaymentDetails = location.state || {};

  useEffect(() => {
    // Redirect to landing page if not authenticated
    if (!isLoading && !isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    document.title = "Obrigado pela sua compra - Tarot Virtual";
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">A carregar...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-900 to-blue-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full mb-6 animate-bounce">
            <svg
              className="w-10 h-10 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>

          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Obrigado pela sua compra!
          </h1>

          <p className="text-purple-200 text-lg">
            A sua compra foi processada e aguarda pagamento.
          </p>
        </div>

        {(paymentDetails.reference || paymentDetails.entity) && (
          <div className="mb-8">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20 shadow-2xl">
              <h2 className="text-2xl font-bold text-white mb-4">
                Referencia Multibanco
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                {paymentDetails.entity && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Entidade
                    </p>
                    <p className="text-white font-semibold text-xl">
                      {paymentDetails.entity}
                    </p>
                  </div>
                )}
                {paymentDetails.reference && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Referencia
                    </p>
                    <p className="text-white font-semibold text-xl">
                      {paymentDetails.reference}
                    </p>
                  </div>
                )}
                {paymentDetails.amount !== undefined && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Valor a Pagar
                    </p>
                    <p className="text-white font-semibold text-xl">
                      €{paymentDetails.amount.toFixed(2)}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Main Card */}
        <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 mb-8 shadow-2xl">
          {/* Confirmation Message */}
          <div className="mb-8 pb-8 border-b border-white/10">
            <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl p-6 border border-green-400/30">
              <p className="text-white text-center leading-relaxed">
                {paymentDetails.phrase}
              </p>
            </div>
          </div>

          {/* What's Next */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-6">E agora?</h2>

            <div className="space-y-4">
              {/* Step 2 */}
              <div className="flex items-start p-4 bg-indigo-600/20 rounded-xl border border-indigo-400/30">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-500 text-white font-bold"></div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-white">
                    Veja o seu destino
                  </h3>
                  <p className="text-indigo-200 mt-1">
                    Navegue para a secção de chat e comece a sua jornada
                    connosco! Descubra o que o futuro lhe reserva num abrir e
                    fechar de olhos!
                  </p>
                </div>
              </div>

              {/* Step 1 */}
              <div className="flex items-start p-4 bg-purple-600/20 rounded-xl border border-purple-400/30">
                <div className="flex-shrink-0">
                  <div className="flex items-center justify-center h-10 w-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white font-bold"></div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-white">
                    Descarregue a sua fatura{" "}
                  </h3>
                  <p className="text-purple-200 mt-1">
                    Encontre os detalhes da sua compra, após pagamento poderá
                    descarregar a sua fatura.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Details */}
          {Object.keys(paymentDetails).length > 0 && (
            <div className="mb-8 pb-8 border-t border-white/10 pt-8">
              <h3 className="text-xl font-bold text-white mb-4">
                Detalhes do Pagamento
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {paymentDetails.orderId && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Número do Pedido
                    </p>
                    <p className="text-white font-semibold">
                      {paymentDetails.orderId}
                    </p>
                  </div>
                )}

                {paymentDetails.packageName && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Plano Adquirido
                    </p>
                    <p className="text-white font-semibold">
                      {paymentDetails.packageName}
                    </p>
                  </div>
                )}

                {paymentDetails.amount && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Valor Pago
                    </p>
                    <p className="text-white font-semibold">
                      €{paymentDetails.amount.toFixed(2)}
                    </p>
                  </div>
                )}

                {paymentDetails.paymentMethod && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Método de Pagamento
                    </p>
                    <p className="text-white font-semibold">
                      {paymentDetails.paymentMethod}
                    </p>
                  </div>
                )}

                {paymentDetails.timestamp && (
                  <div className="bg-white/5 rounded-lg p-4 border border-white/10 md:col-span-2">
                    <p className="text-purple-300 text-sm uppercase tracking-wide mb-1">
                      Data da Compra
                    </p>
                    <p className="text-white font-semibold">
                      {new Date(paymentDetails.timestamp).toLocaleString(
                        "pt-PT"
                      )}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate("/user-dashboard")}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl"
          >
            Aceder ao Dashboard
          </button>

          <button
            onClick={() => navigate("/")}
            className="px-8 py-4 bg-white/10 border-2 border-white/30 text-white font-bold rounded-lg hover:bg-white/20 transition-all"
          >
            Começar a consultar
          </button>
        </div>

        {/* Support Note */}
        <div className="text-center mt-8">
          <p className="text-purple-200 text-sm">
            Tem dúvidas? Entre em contacto com o nosso{" "}
            <a
              href="/"
              className="text-purple-300 hover:text-purple-100 underline"
            >
              suporte ao cliente
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ThankYouPage;
